import { User } from "lucide-react";
import { cn } from "@/lib/utils/ui";
import { toSentenceCase } from "@/lib/utils/typeUtils";
import barbellUrl from "@/assets/equipment/barbell.svg";
import cablesUrl from "@/assets/equipment/cables.svg";
import dumbbellUrl from "@/assets/equipment/dumbbell.svg";
import exerciseBallUrl from "@/assets/equipment/exercise-ball.svg";
import ezBarUrl from "@/assets/equipment/ez-bar.svg";
import foamRollUrl from "@/assets/equipment/foam-roll.svg";
import kettlebellUrl from "@/assets/equipment/kettlebell.svg";
import machineUrl from "@/assets/equipment/machine.svg";
import medicineBallUrl from "@/assets/equipment/medicine-ball.svg";
import resistanceBandUrl from "@/assets/equipment/resistance-band.svg";

const EQUIPMENT_ICON_URLS: Record<string, string> = {
  dumbbell: dumbbellUrl,
  barbell: barbellUrl,
  kettlebells: kettlebellUrl,
  machine: machineUrl,
  cable: cablesUrl,
  bands: resistanceBandUrl,
  "e-z curl bar": ezBarUrl,
  "exercise ball": exerciseBallUrl,
  "medicine ball": medicineBallUrl,
  "foam roll": foamRollUrl,
};

interface EquipmentIconProps {
  equipment: string;
  className?: string;
}

export function EquipmentIcon({ equipment, className }: EquipmentIconProps) {
  const key = equipment.toLowerCase();

  if (key === "body only") {
    return (
      <User
        aria-hidden
        className={cn("size-4 shrink-0 text-muted-foreground", className)}
      />
    );
  }

  const src = EQUIPMENT_ICON_URLS[key];
  if (!src) {
    return null;
  }

  const maskImage = `url("${src}")`;

  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-4 shrink-0 bg-current text-muted-foreground",
        className,
      )}
      style={{
        maskImage,
        WebkitMaskImage: maskImage,
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskPosition: "center",
        maskSize: "contain",
        WebkitMaskSize: "contain",
      }}
    />
  );
}

interface EquipmentLabelProps {
  equipment: string;
  label?: string;
  className?: string;
}

export function EquipmentLabel({
  equipment,
  label,
  className,
}: EquipmentLabelProps) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className="flex size-4 shrink-0 items-center justify-center">
        <EquipmentIcon equipment={equipment} />
      </span>
      {label ?? toSentenceCase(equipment)}
    </span>
  );
}
